import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { ArrowUpRight, Delete } from '@sweep-io/sweep-design/dist/icons';
import { colors, Typography, IconButton, Button } from '@sweep-io/sweep-design';
import { SweepTypographyVariants } from '@sweep-io/sweep-design/dist/components/Typography/types';
import { IconSize } from '@sweep-io/sweep-design/dist/icons/common';
import { linearGradient } from '../../table/utils';
import { Prompt } from '@server/prompts';
import { useState } from 'react';

const HOVER_BG = colors.grey[100];

const styles: {
  [key: string]: {
    titleTypography: SweepTypographyVariants;
    titleTypographyColor: string;
    padding: string;
    iconSize: IconSize;
    iconPaddingTop: string;
    gap: string;
    alignItems: string;
  };
} = {
  small: {
    titleTypography: 'caption',
    titleTypographyColor: colors.grey[700],
    padding: '12px',
    iconSize: 'medium',
    iconPaddingTop: '0px',
    gap: '8px',
    alignItems: 'center',
  },
  large: {
    titleTypography: 'body',
    titleTypographyColor: colors.grey[800],
    padding: '24px 8px',
    iconSize: 'large',
    iconPaddingTop: '2px',
    gap: '16px',
    alignItems: 'flex-start',
  },
};

export enum PromptWithArrowActions {
  Edit = 'edit',
  Duplicate = 'duplicate',
  Delete = 'delete',
}

export const PromptWithArrow = ({
  onClick,
  onActionClick,
  prompt,
  isDisabled,
  variant = 'small',
}: {
  onClick: () => void;
  onActionClick?: (
    action: PromptWithArrowActions,
    prompt: Pick<Prompt, 'id' | 'name' | 'promptText'>,
  ) => void;
  prompt: Pick<Prompt, 'id' | 'name' | 'promptText'>;
  isDisabled: boolean;
  variant?: 'small' | 'large';
}) => {
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const { name, promptText: description } = prompt;
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        padding: styles[variant].padding,
        display: 'flex',
        gap: styles[variant].gap,
        alignItems: styles[variant].alignItems,
        cursor: isDisabled ? 'default' : 'pointer',
        pointerEvents: isDisabled ? 'none' : undefined,
        borderBottom: `1px solid ${colors.grey[120]}`,
        ':hover': {
          backgroundColor: isDisabled ? undefined : HOVER_BG,
          '.prompt-actions': {
            display: 'flex',
          },
        },
      }}
      onClick={onClick}
    >
      <Stack sx={{ paddingTop: styles[variant].iconPaddingTop }}>
        <ArrowUpRight color={colors.grey[700]} size={styles[variant].iconSize} />
      </Stack>
      <Stack>
        <Typography
          variant={styles[variant].titleTypography}
          color={styles[variant].titleTypographyColor}
          textAlign="start"
        >
          {name}
        </Typography>
        {description && variant === 'large' && (
          <Box sx={{ whiteSpace: 'pre-line' }}>
            {/* pre-line so new line (/n) can be rendered properly */}
            <Typography variant="caption" color={colors.grey[700]} textAlign="start">
              {description}
            </Typography>
          </Box>
        )}
      </Stack>

      {onActionClick && (
        <Stack
          className="prompt-actions"
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            margin: '0 auto',
            height: '100%',
            background: linearGradient(HOVER_BG),
            pl: 2,
            pr: 2,
            display: 'none',
          }}
        >
          <IconButton
            size="small"
            variant="system"
            loading={isLoading[PromptWithArrowActions.Delete]}
            onClick={async (e) => {
              e.stopPropagation();
              setIsLoading({ [PromptWithArrowActions.Delete]: true });
              await onActionClick(PromptWithArrowActions.Delete, prompt);
              setIsLoading({ [PromptWithArrowActions.Delete]: false });
            }}
          >
            <Delete />
          </IconButton>

          <Button
            size="small"
            variant="system"
            loading={isLoading[PromptWithArrowActions.Duplicate]}
            onClick={async (e) => {
              e.stopPropagation();
              setIsLoading({ [PromptWithArrowActions.Duplicate]: true });
              await onActionClick(PromptWithArrowActions.Duplicate, prompt);
              setIsLoading({ [PromptWithArrowActions.Duplicate]: false });
            }}
          >
            Duplicate
          </Button>

          <Button
            size="small"
            variant="system"
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(PromptWithArrowActions.Edit, prompt);
            }}
          >
            Edit
          </Button>
        </Stack>
      )}
    </Box>
  );
};
